// src/components/tribunal/FormTribunal/FormTribunal.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Modal, Spin } from "antd";
import { useNavigate } from "react-router-dom";
import { notify } from "../../ui/notify";

import "../tribunal/FormTribunal.css";
import "../../modals/tribunal/selectAddNew.css";

import { actionConciliacionSearchExpedientes } from "../../../redux/actions/conciliacion/conciliacion";
import { actionAutoridadesTribunalGet } from "../../../redux/actions/autoridades/autoridades";
import useLaboralCatalogos from "../../../containers/pages/materias/laboral/useLaboralCatalogos";
import { actionEmpresasGet, actionEmpresaCreate } from "../../../redux/actions/empresas/empresas";
import { actionCatalogoConfigGet } from "../../../redux/actions/catalogos_config/catalogos_config";
import { medioElectronicoLabel } from "../../../utils/tribunalCatalogos";

import { getDefaults, mergeInitialValues } from "./formTribunal.defaults";
import { collapseSpaces, anyTrue, isEmpty, isValidISODate, toNum } from "./formTribunal.helpers";
import useExpedienteAutocomplete from "./useExpedienteAutocomplete";

import DatosGeneralesSection from "./DatosGeneralesSection";
import FormEmpresas from "../empresas/FormEmpresas";
import EtapasCitatorioSection from "./EtapasCitatorioSection";
import AccionesPrestacionesSection from "./AccionesPrestacionesSection";
import HechosSection from "./HechosSection";
import CondicionesTrabajoSection from "./CondicionesTrabajoSection";
import PruebasSection from "./PruebasSection";
import { PATH } from "../../../redux/utils";
const genRowId = () => `row_${Date.now()}_${Math.random().toString(16).slice(2)}`;

const safeArr = (v) => (Array.isArray(v) ? v : []);
const safeObj = (v) => (v && typeof v === "object" ? v : {});

export default function FormTribunal({
  isEdit,
  idCiudad,
  idExpediente,
  initialValues,
  idAutoridad,
  nombreIdentificacionCiudad,
  modoRapido = false,
  onToggleModoRapido,
  onCancel,
  onSubmit,
  prefillRazonSocialIds = [],
  prefillRazonSocialNombres = [],
}) {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const api = notify;
  const defaults = useMemo(() => getDefaults(), []);

  const [form, setForm] = useState(() => mergeInitialValues(defaults, initialValues));
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const [autofillOpen, setAutofillOpen] = useState(false);
  const [autofillText, setAutofillText] = useState("");
  const [autofillError, setAutofillError] = useState("");
  const [aiFields, setAiFields] = useState(() => new Set());
  const [reviewFields, setReviewFields] = useState(() => new Map());

  const [catalogOverrides, setCatalogOverrides] = useState({});
  const [newEmpresaNombre, setNewEmpresaNombre] = useState("");
  const [newRazonSocialNombre, setNewRazonSocialNombre] = useState("");

  // Modal "¿Empresa nueva? Créala aquí" (mismo comportamiento que Centro de Conciliación)
  const [nuevaEmpresaModal, setNuevaEmpresaModal] = useState(false);
  const [savingNuevaEmpresa, setSavingNuevaEmpresa] = useState(false);
  const nuevaEmpresaRef = useRef(null);

  const autoridadesSlice = useSelector((state) => state?.autoridades ?? {});
  const empresasSlice = useSelector((state) => state?.empresas ?? {});
  const estadosItemsAll = useSelector((state) => state?.estados?.data?.items ?? []);
  const ciudadesItemsAll = useSelector((state) => state?.ciudades?.data?.items ?? []);

  // =========================
  // Helpers coerce
  // =========================
  const coerceEmpresasArray = (slice) => {
    if (Array.isArray(slice)) return slice;
    if (Array.isArray(slice?.items)) return slice.items;
    if (Array.isArray(slice?.data)) return slice.data;
    if (Array.isArray(slice?.list)) return slice.list;
    if (Array.isArray(slice?.data?.items)) return slice.data.items;
    return [];
  };

  const coerceItems = (slice) => {
    const a = slice?.data?.items;
    if (Array.isArray(a)) return a;
    const b = slice?.items;
    if (Array.isArray(b)) return b;
    return [];
  };

  const pickCompanyName = (it) =>
    it?.nombre ?? it?.nombre_cliente ?? it?.empresa ?? it?.name ?? it?.razon_social ?? "";
  const pickCompanyId = (it) => it?.id_empresa ?? it?.id ?? null;

  const buildRSOption = ({ label, value, empresa_id }) => ({
    label: collapseSpaces(label || ""),
    value: value ?? collapseSpaces(label || ""),
    empresa_id: empresa_id ?? null,
  });

  const normLabelLocal = (txt) => collapseSpaces(txt || "").trim().toLowerCase();

  const dedupRazones = (arr = []) => {
    const map = new Map();
    for (const r of arr) {
      const key = `${normLabelLocal(r.label)}|${r.empresa_id ?? "null"}`;
      const existing = map.get(key);
      if (!existing) map.set(key, r);
      else if (existing.value == null && r.value != null) map.set(key, r);
    }
    return Array.from(map.values()).sort((a, b) => a.label.localeCompare(b.label, "es"));
  };

  const buildEmpresasIndex = (items = []) => {
    const byName = new Map();
    if (!Array.isArray(items)) return byName;

    items.forEach((it) => {
      const name = collapseSpaces(pickCompanyName(it));
      if (!name) return;

      const idEmpresa = pickCompanyId(it);
      if (!byName.has(name)) byName.set(name, { empresaIds: new Set(), razones: [] });
      const bucket = byName.get(name);

      if (idEmpresa != null) bucket.empresaIds.add(idEmpresa);

      const rsArr = Array.isArray(it?.razones_sociales) ? it.razones_sociales : [];
      rsArr.forEach((rs) => {
        const label = rs?.nombre ?? rs?.razon_social ?? rs?.name ?? "";
        const value = rs?.id_empresa_razon_social ?? rs?.id ?? null;
        const empresa_id = rs?.id_empresa ?? idEmpresa ?? null;
        if (!label) return;
        bucket.razones.push(buildRSOption({ label, value, empresa_id }));
      });

      const plainRS =
        it?.razon_social && String(it.razon_social).trim() !== "" ? String(it.razon_social) : null;
      if (plainRS) bucket.razones.push(buildRSOption({ label: plainRS, value: null, empresa_id: idEmpresa }));
    });

    for (const [name, bucket] of byName.entries()) {
      bucket.razones = dedupRazones(bucket.razones);
      byName.set(name, bucket);
    }
    return byName;
  };

  const empresasItems = useMemo(() => coerceEmpresasArray(empresasSlice), [empresasSlice]);
  const empresasIndex = useMemo(() => buildEmpresasIndex(empresasItems), [empresasItems]);

  // =========================
  // Estado/catálogos
  // =========================
  const [selectedEstadoId, setSelectedEstadoId] = useState(null);
  const [selectedCiudadId, setSelectedCiudadId] = useState(null);
  const [selectedAutoridadId, setSelectedAutoridadId] = useState(null);
  const [selectedAutoridadData, setSelectedAutoridadData] = useState(null);

  const { estadosOptions, ciudadesOptions, estadosById, ciudadesById } = useLaboralCatalogos(
    selectedEstadoId,
    selectedCiudadId,
    { modulo: "tribunal" }
  );

  useEffect(() => {
    dispatch(actionAutoridadesTribunalGet({}));
  }, [dispatch]);

  useEffect(() => {
    dispatch(actionEmpresasGet());
  }, [dispatch]);

  const [mediosElectronicosCatalogo, setMediosElectronicosCatalogo] = useState([]);
  const [accionesIntentadasCatalogo, setAccionesIntentadasCatalogo] = useState([]);
  const [prestacionesCatalogo, setPrestacionesCatalogo] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const data = await dispatch(actionCatalogoConfigGet("medios_electronicos"));
        const items = Array.isArray(data?.items) ? data.items : [];
        setMediosElectronicosCatalogo(
          items.map((i) => ({ label: medioElectronicoLabel(i.nombre), value: i.nombre }))
        );
      } catch (e) {
        notify.error({
          message: "Error al cargar medios electrónicos",
          description: e?.message || "Sin detalle",
          placement: "bottomRight",
        });
      }
    })();

    (async () => {
      try {
        const data = await dispatch(actionCatalogoConfigGet("acciones_intentadas"));
        const items = Array.isArray(data?.items) ? data.items : [];
        setAccionesIntentadasCatalogo(items.map((i) => i.nombre));
      } catch (e) {
        notify.error({
          message: "Error al cargar acciones intentadas",
          description: e?.message || "Sin detalle",
          placement: "bottomRight",
        });
      }
    })();

    (async () => {
      try {
        const data = await dispatch(actionCatalogoConfigGet("prestaciones_reclamadas"));
        const items = Array.isArray(data?.items) ? data.items : [];
        setPrestacionesCatalogo(items.map((i) => i.nombre));
      } catch (e) {
        notify.error({
          message: "Error al cargar prestaciones reclamadas",
          description: e?.message || "Sin detalle",
          placement: "bottomRight",
        });
      }
    })();
  }, [dispatch]);

  // =========================
  // setters
  // =========================
  const setField = (name, value) => {
    setForm((p) => ({ ...p, [name]: value }));
    setErrors((e) => {
      if (!e[name]) return e;
      const copy = { ...e };
      delete copy[name];
      return copy;
    });
    setReviewFields((prev) => {
      if (!prev.has(name)) return prev;
      const next = new Map(prev);
      next.delete(name);
      return next;
    });
  };

  const setPruebasDetalle = (key, valueObj) => {
    setForm((p) => ({
      ...p,
      pruebas_detalle: {
        ...(p.pruebas_detalle || {}),
        actora: {
          ...((p.pruebas_detalle && p.pruebas_detalle.actora) || {}),
          [key]: valueObj,
        },
      },
    }));
  };

  const setPrestacion = (key, checked) => {
    setForm((p) => ({
      ...p,
      prestaciones: { ...p.prestaciones, [key]: checked },
    }));
    setErrors((e) => {
      if (!e.prestaciones) return e;
      const copy = { ...e };
      delete copy.prestaciones;
      return copy;
    });
  };

  // =========================
  // Hidratar en edit
  // =========================
  useEffect(() => {
    if (!initialValues) return;
    const iv = initialValues || {};

    setForm(mergeInitialValues(defaults, iv));

    if (iv.estado != null && String(iv.estado).trim() !== "") setSelectedEstadoId(Number(iv.estado));
    if (iv.ciudad != null && String(iv.ciudad).trim() !== "") setSelectedCiudadId(Number(iv.ciudad));
    if (iv.autoridad != null && String(iv.autoridad).trim() !== "") setSelectedAutoridadId(String(iv.autoridad));
  }, [initialValues, defaults, isEdit]);

  // =========================
  // Sincronizar selected* => form
  // =========================
  useEffect(() => {
    if (selectedEstadoId != null) setField("estado", selectedEstadoId);
    if (selectedCiudadId != null) setField("ciudad", selectedCiudadId);
    if (selectedAutoridadId != null) setField("autoridad", selectedAutoridadId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEstadoId, selectedCiudadId, selectedAutoridadId]);

  // =========================
  // Filtrar autoridades (tipo 2 tribunal)
  // =========================
  const autoridadesItems = useMemo(() => {
    const items = coerceItems(autoridadesSlice);
    return (items || []).filter((au) => Number(au?.id_tipo_autoridad) === 2);
  }, [autoridadesSlice]);

  const ciudadIdsConAutoridad = useMemo(() => {
    const s = new Set();
    (autoridadesItems || []).forEach((au) => {
      const idc = au?.id_ciudad;
      if (idc !== undefined && idc !== null && String(idc) !== "") s.add(Number(idc));
    });
    return s;
  }, [autoridadesItems]);

  const estadoIdsConAutoridad = useMemo(() => {
    const s = new Set();
    ciudadIdsConAutoridad.forEach((idCiudadX) => {
      const c = ciudadesById?.[String(idCiudadX)] || ciudadesById?.[Number(idCiudadX)];
      const idEstado = c?.id_estado ?? c?.idEstado ?? null;
      if (idEstado !== undefined && idEstado !== null && String(idEstado) !== "") s.add(Number(idEstado));
    });
    return s;
  }, [ciudadIdsConAutoridad, ciudadesById]);

  const estadosOptionsFinal = useMemo(() => {
    const base = Array.isArray(estadosOptions) ? estadosOptions : [];
    const autoridadesReady = Array.isArray(autoridadesItems) && autoridadesItems.length > 0;
    const ciudadesReady = ciudadesById && Object.keys(ciudadesById).length > 0;
    if (!autoridadesReady || !ciudadesReady) return base;
    return base.filter((o) => estadoIdsConAutoridad.has(Number(o.value)));
  }, [estadosOptions, estadoIdsConAutoridad, autoridadesItems, ciudadesById]);

  const ciudadesOptionsFinal = useMemo(() => {
    const base = Array.isArray(ciudadesOptions) ? ciudadesOptions : [];
    if (!selectedEstadoId) return [];
    const autoridadesReady = Array.isArray(autoridadesItems) && autoridadesItems.length > 0;
    const ciudadesReady = ciudadesById && Object.keys(ciudadesById).length > 0;
    if (!autoridadesReady || !ciudadesReady) return base;
    return base.filter((o) => ciudadIdsConAutoridad.has(Number(o.value)));
  }, [ciudadesOptions, ciudadIdsConAutoridad, selectedEstadoId, autoridadesItems, ciudadesById]);

  const autoridadOptions = useMemo(() => {
    return (autoridadesItems || [])
      .filter((au) => String(au?.id_ciudad || "") === String(selectedCiudadId || ""))
      .map((au) => ({
        label: collapseSpaces(au?.nombre || ""),
        value: String(au?.id ?? ""),
        data: au,
      }))
      .filter((o) => o.label && o.value);
  }, [autoridadesItems, selectedCiudadId]);

  const handleChangeEstado = (value) => {
    const v = value ?? null;
    setSelectedEstadoId(v);
    setSelectedCiudadId(null);
    setSelectedAutoridadId(null);
    setSelectedAutoridadData(null);
    setField("estado", v);
    setField("ciudad", null);
    setField("autoridad", null);
  };

  const handleChangeCiudad = (value) => {
    const v = value ?? null;
    setSelectedCiudadId(v);
    setSelectedAutoridadId(null);
    setSelectedAutoridadData(null);
    setField("ciudad", v);
    setField("autoridad", null);
  };

  const handleChangeAutoridad = (value) => {
    const v = value ?? "";
    setSelectedAutoridadId(v);
    setField("autoridad", v);
    setErrors((e) => {
      if (!e.autoridad) return e;
      const c = { ...e };
      delete c.autoridad;
      return c;
    });
  };

  // =========================
  // Options empresas
  // =========================
  const empresaNombreOptions = useMemo(() => {
    const names = Array.from(empresasIndex.keys());
    return names.map((n) => ({ label: n, value: n })).sort((a, b) => a.label.localeCompare(b.label, "es"));
  }, [empresasIndex]);

  const allRazonesSociales = useMemo(() => {
    const all = [];
    for (const [, bucket] of empresasIndex.entries()) {
      all.push(...bucket.razones);
    }
    return dedupRazones(all);
  }, [empresasIndex]);

  // Aplicar razón social al cargar el catálogo de empresas (prefill desde conciliación)
  const prefillRSRef = React.useRef(false);
  useEffect(() => {
    if (isEdit) return;
    if (!allRazonesSociales?.length) return;
    if (prefillRSRef.current) return;

    const hasIds = Array.isArray(prefillRazonSocialIds) && prefillRazonSocialIds.length > 0;
    const hasNames = Array.isArray(prefillRazonSocialNombres) && prefillRazonSocialNombres.length > 0;
    if (!hasIds && !hasNames) return;

    prefillRSRef.current = true;

    let matched = [];

    // Intentar match por id_razon_social (= id_empresa_razon_social del catálogo)
    if (hasIds) {
      matched = prefillRazonSocialIds
        .map((id) => {
          const opt = allRazonesSociales.find(
            (o) => o.value != null && String(o.value) === String(id)
          );
          return opt ? String(opt.value) : null;
        })
        .filter(Boolean);
    }

    // Fallback: match por nombre normalizado
    if (!matched.length && hasNames) {
      matched = prefillRazonSocialNombres
        .map((nombre) => {
          const normalizado = collapseSpaces(nombre).toLowerCase();
          const opt = allRazonesSociales.find(
            (o) => collapseSpaces(o.label).toLowerCase() === normalizado
          );
          return opt ? String(opt.value == null ? opt.label : opt.value) : null;
        })
        .filter(Boolean);
    }

    if (matched.length) {
      setField("empresa_razon_social_ids", matched);
      const chosen = allRazonesSociales.find(
        (o) => String(o.value == null ? o.label : o.value) === matched[0]
      );
      if (chosen?.empresa_id != null) setField("empresa_id", chosen.empresa_id);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allRazonesSociales]);

  const handleChangeEmpresaNombre = (value) => {
    const v = collapseSpaces(value || "");
    setField("empresa_nombre", v);
    setField("empresa_razon_social_ids", []);
    setField("empresa_id", null);

    setField("cliente_directo", 1);
    setField("corresponsal_nombre", "No hay corresponsal");
    setField("corresponsal_celular", "");
    setField("corresponsal_correo", "");
  };

  const handleChangeRazonSocial = (values) => {
    const arr = Array.isArray(values) ? values : [];
    setField("empresa_razon_social_ids", arr);

    const first = arr[0];
    if (first == null) return;

    const chosen = allRazonesSociales.find((o) => String(o.value == null ? o.label : o.value) === String(first));
    if (chosen?.empresa_id != null) setField("empresa_id", chosen.empresa_id);
  };

  // =========================
  // Hook autocomplete expedientes
  // =========================
  const {
    expedienteSearchValue,
    expedienteOptions,
    expedienteSearching,
    handleExpedienteFocus,
    handleExpedienteSearch,
    handleExpedienteSelect,
    handleExpedienteChange,
  } = useExpedienteAutocomplete({
    dispatch,
    actionConciliacionSearchExpedientes,
    setField,
    setSelectedCiudadId,
    setSelectedEstadoId,
    ciudadesById,
    empresasIndex,
    api,
  });

  // =========================
  // CITATORIO (archivos)
  // =========================
  const citatorioInputRef = useRef(null);

  const openCitatorioPicker = () => {
    if (citatorioInputRef.current) citatorioInputRef.current.click();
  };

  const onCitatorioPicked = (e) => {
    const list = Array.from(e?.target?.files || []);
    if (!list.length) return;

    setForm((p) => {
      const prev = safeArr(p.citatorio);
      const next = [...prev];

      for (const f of list) {
        next.push({
          id: genRowId(),
          filename: f.name,
          categoria: "DEMANDA/NOTIFICACION",
          estado: "nuevo",
          _file: f,
        });
      }
      return { ...p, citatorio: next };
    });

    setErrors((er) => {
      if (!er.citatorio) return er;
      const c = { ...er };
      delete c.citatorio;
      return c;
    });

    e.target.value = "";
  };

  const viewCitatorio = (row) => {
    if (!row) return;

    if (row._file) {
      const url = URL.createObjectURL(row._file);
      window.open(url, "_blank", "noopener,noreferrer");
      setTimeout(() => URL.revokeObjectURL(url), 30000);
      return;
    }
    console.log(PATH)
    console.log(row.url)

    if (row.url) {
      window.open(PATH+`/uploads/tribunal/${idExpediente}/${row.url}`, "_blank", "noopener,noreferrer");
      return;
    }

    api.info({
      message: "Documento",
      description: "No hay archivo local ni URL para previsualizar.",
      placement: "bottomRight",
      duration: 3,
    });
  };

  const deleteCitatorio = (rowId) => {
    setForm((p) => {
      const prev = safeArr(p.citatorio);
      const next = prev.filter((x) => String(x.id) !== String(rowId));
      return { ...p, citatorio: next };
    });
  };

  // =========================
  // PRUEBAS: checklist + helpers UI
  // =========================
  const MEDIOS_OPTS = mediosElectronicosCatalogo;

  const collapseSpacesLocal = (s) => collapseSpaces(s || "");

  const makeNewSelectValue = (label) => {
    const clean = collapseSpacesLocal(label);
    const stamp = `${Date.now()}_${Math.random().toString(16).slice(2)}`;
    return `new:${clean}:${stamp}`;
  };

  const toggleChecklistActora = (key) => {
    setForm((p) => {
      const prev = safeObj(p.pruebas_checklist);
      const act = safeObj(prev.actora);
      return {
        ...p,
        pruebas_checklist: {
          ...prev,
          actora: { ...act, [key]: !act[key] },
        },
      };
    });

    setErrors((e) => {
      if (!e.pruebas_checklist) return e;
      const c = { ...e };
      delete c.pruebas_checklist;
      return c;
    });
  };
const checklistActivo = useMemo(() => {
  const act = safeObj(form?.pruebas_checklist?.actora);
  return {
    confesional: !!act.confesional,
    confesional_hechos_propios: !!act.confesional_hechos_propios,
    testimonial: !!act.testimonial,
    medios_electronicos: !!act.medios_electronicos,
    documental_publica: !!act.documental_publica,
    documental_privada: !!act.documental_privada,
    informe_autoridad: !!act.informe_autoridad,
    documental: !!act.documental,
    pericial: !!act.pericial,
    inspeccion_ocular: !!act.inspeccion_ocular,
  };
}, [form?.pruebas_checklist?.actora]);
  const addTestigo = () => {
    setForm((p) => {
      const pd = safeObj(p.pruebas_detalle);
      const act = safeObj(pd.actora);
      const t = safeObj(act.testimonial);

      const nombre = collapseSpacesLocal(t.draft_nombre || "");
      if (!nombre) return p;

      const prevList = safeArr(t.testigos);
      const nextList = [...prevList, { id: genRowId(), nombre }];

      return {
        ...p,
        pruebas_detalle: {
          ...pd,
          actora: {
            ...act,
            testimonial: {
              ...t,
              draft_nombre: "",
              testigos: nextList,
            },
          },
        },
      };
    });
  };

  const removeTestigo = (id) => {
    setForm((p) => {
      const pd = safeObj(p.pruebas_detalle);
      const act = safeObj(pd.actora);
      const t = safeObj(act.testimonial);

      const prevList = safeArr(t.testigos);
      const nextList = prevList.filter((x) => String(x.id) !== String(id));

      return {
        ...p,
        pruebas_detalle: {
          ...pd,
          actora: { ...act, testimonial: { ...t, testigos: nextList } },
        },
      };
    });
  };

  const setMediosElectronicosTipos = (arr) => {
    const next = safeArr(arr);
    setPruebasDetalle("medios_electronicos", {
      ...(safeObj(form?.pruebas_detalle?.actora?.medios_electronicos) || {}),
      tipos: next,
    });
  };

  const toggleMedioElectronico = (value) => {
    const cur = safeArr(form?.pruebas_detalle?.actora?.medios_electronicos?.tipos);
    const exists = cur.includes(value);
    const next = exists ? cur.filter((x) => x !== value) : [...cur, value];
    setMediosElectronicosTipos(next);
  };

  // =========================
  // Validación
  // =========================
  const validate = useCallback(() => {
    const e = {};
    const req = (key, msg = "Requerido") => {
      if (e[key]) return;
      e[key] = msg;
    };
    const reqText = (key, msg = "Requerido") => {
      if (isEmpty(form?.[key])) req(key, msg);
    };
    const reqDate = (key) => {
      const v = form?.[key];
      if (isEmpty(v)) req(key, "Fecha requerida");
      else if (!isValidISODate(v)) req(key, "Fecha inválida");
    };

    if (selectedEstadoId == null || String(selectedEstadoId).trim() === "") req("estado");
    if (selectedCiudadId == null || String(selectedCiudadId).trim() === "") req("ciudad");
    if (selectedAutoridadId == null || String(selectedAutoridadId).trim() === "") req("autoridad");

    reqDate("fecha_limite_contestacion");

    reqDate("fecha_notificacion_demanda");

 
    reqText("numero_expediente", "Requerido");
    reqText("nombre_parte_actora", "Requerido");

    if (!Array.isArray(form?.empresa_razon_social_ids) || form.empresa_razon_social_ids.length === 0) {
      req("empresa_razon_social_ids", "Requerido");
    }

    if (Number(form?.cliente_directo || 1) !== 1) reqText("corresponsal_nombre", "Requerido");

   // reqText("accion_intentada", "Requerido");

    console.log(form)
    reqText("accion_intentada", "Requerido");

  //  reqText("hechos", "Requerido");
    setErrors(e);

    if (Object.keys(e).length > 0) {
      return false;
    }
    return true;
  }, [form, selectedEstadoId, selectedCiudadId, selectedAutoridadId]);

  // =========================
  // payload builder + submit
  // =========================
  const buildPayloadAndFiles = useCallback(() => {
    const packRows = (rows = []) =>
      (rows || []).map((x) => ({
        id: x.id,
        filename: x.filename,
        categoria: x.categoria,
        estado: x.estado,
      }));

    const citatorioRows = Array.isArray(form?.citatorio) ? form.citatorio : [];

    const filesMap = {
      citatorio: citatorioRows.filter((x) => x?._file).map((x) => ({ id: x.id, file: x._file })),
      evidencias_actora: (form.evidencias_actora || [])
        .filter((x) => x?._file)
        .map((x) => ({ id: x.id, file: x._file })),
    };

    const actoraDetalle = { ...(form?.pruebas_detalle?.actora || {}) };

    const confesionalRegistros = Array.isArray(actoraDetalle?.confesional?.registros)
      ? actoraDetalle.confesional.registros
      : [];

    const testimonialTestigos = Array.isArray(actoraDetalle?.testimonial?.testigos)
      ? actoraDetalle.testimonial.testigos
      : [];

    const documentalPublicaDocs = Array.isArray(actoraDetalle?.documental_publica?.documentos)
      ? actoraDetalle.documental_publica.documentos
      : [];

    const documentalPrivadaDocs = Array.isArray(actoraDetalle?.documental_privada?.documentos)
      ? actoraDetalle.documental_privada.documentos
      : [];
const informeAutoridadDocs = Array.isArray(actoraDetalle?.informe_autoridad?.documentos)
  ? actoraDetalle.informe_autoridad.documentos
  : [];

    const pericialRegistros = Array.isArray(actoraDetalle?.pericial)
      ? actoraDetalle.pericial
      : Array.isArray(actoraDetalle?.pericial?.registros)
      ? actoraDetalle.pericial.registros
      : [];

    const inspeccionOcularRegistros = Array.isArray(actoraDetalle?.inspeccion_ocular)
      ? actoraDetalle.inspeccion_ocular
      : Array.isArray(actoraDetalle?.inspeccion_ocular?.registros)
      ? actoraDetalle.inspeccion_ocular.registros
      : [];

if (actoraDetalle?.inspeccion_ocular) {
    actoraDetalle.inspeccion_ocular = inspeccionOcularRegistros.map((r) => ({
    tipo_inspeccion_ocular: Array.isArray(r?.tipo_inspeccion_ocular)
      ? r.tipo_inspeccion_ocular
      : r?.tipo_inspeccion_ocular
      ? [r.tipo_inspeccion_ocular]
      : [],
    nombre_inspeccion_ocular: r?.nombre_inspeccion_ocular || "",
    objeto_prueba: r?.objeto_prueba || "",
  }));
}

    if (actoraDetalle?.testimonial) {
      actoraDetalle.testimonial = {
        testigos: testimonialTestigos.map((t) => ({
          nombre: t?.nombre || "",
          relacion: t?.relacion || "",
          contacto: t?.contacto || "",
        })),
      };
    }

    if (actoraDetalle?.documental_publica) {
      actoraDetalle.documental_publica = {
        documentos: documentalPublicaDocs.map((d) => ({
          nombre: d?.nombre || "",
          descripcion: d?.descripcion || "",
          fecha: d?.fecha || null,
        })),
      };
    }

    if (actoraDetalle?.documental_privada) {
      actoraDetalle.documental_privada = {
        documentos: documentalPrivadaDocs.map((d) => ({
          nombre: d?.nombre || "",
          descripcion: d?.descripcion || "",
          fecha: d?.fecha || null,
        })),
      };
    }
if (actoraDetalle?.informe_autoridad) {
  actoraDetalle.informe_autoridad = {
    documentos: informeAutoridadDocs.map((d) => ({
      nombre: d?.nombre || "",
      descripcion: d?.descripcion || "",
      fecha: d?.fecha || null,
    })),
  };
}
   if (actoraDetalle?.pericial) {
  actoraDetalle.pericial = pericialRegistros.map((r) => ({
    tipo_pericial: Array.isArray(r?.tipo_pericial)
      ? r.tipo_pericial
      : r?.tipo_pericial
      ? [r.tipo_pericial]
      : [],
    nombre_perito: r?.nombre_perito || "",
    objeto_prueba: r?.objeto_prueba || "",
  }));
}

   if (actoraDetalle?.inspeccion_ocular) {
  actoraDetalle.inspeccion_ocular = inspeccionOcularRegistros.map((r) => ({
    tipo_inspeccion_ocular: Array.isArray(r?.tipo_inspeccion_ocular)
      ? r.tipo_inspeccion_ocular
      : r?.tipo_inspeccion_ocular
      ? [r.tipo_inspeccion_ocular]
      : [],
    nombre_inspeccion_ocular: r?.nombre_inspeccion_ocular || "",
    objeto_prueba: r?.objeto_prueba || "",
  }));
}
    const payload = {
      meta: {
        isEdit: Boolean(isEdit),
        idExpediente: idExpediente ?? null,
        idCiudad: idCiudad ?? null,
        idAutoridad: idAutoridad ?? null,
        nombreIdentificacionCiudad: nombreIdentificacionCiudad ?? "",
        ...(isEdit ? {} : { carga_rapida: modoRapido ? 1 : 0 }),
      },
      datos_generales: {
        numero_expediente: form.numero_expediente,
        nombre_parte_actora: form.nombre_parte_actora,
        empresa_id: form.empresa_id,
        empresa_nombre: form.empresa_nombre,
        empresa_razon_social_ids: Array.isArray(form.empresa_razon_social_ids) ? form.empresa_razon_social_ids : [],
        empresa_new: null,
        razones_sociales_new: [],
        estado: form.estado,
        ciudad: form.ciudad,
        autoridad: form.autoridad,
        fecha_notificacion_demanda: form.fecha_notificacion_demanda || null,
        fecha_constancia_conciliacion: form.fecha_constancia_conciliacion || null,
        fecha_emision_citatorio: form.fecha_emision_citatorio || null,
        fecha_radicacion_demanda: form.fecha_radicacion_demanda || null,
        fecha_recepcion_demanda: form.fecha_recepcion_demanda || null,
        dias_limite: form.dias_limite|| null,
        fecha_presentacion_demanda: form.fecha_presentacion_demanda || null,
        num_unico: form.num_unico,
        ...(isEdit ? {} : { carga_rapida: modoRapido ? 1 : 0 }),
      },
      etapas_procesales: {
        fecha_limite_contestacion: form.fecha_limite_contestacion || null,
        fecha_audiencia_conciliatoria: form.fecha_audiencia_conciliatoria || null,
        observaciones_etapa_actual: form.observaciones_etapa_actual || "",
      },
      acciones_prestaciones: {
        accion_intentada: form.accion_intentada,
        prestaciones: { ...form.prestaciones },
        otras_prestaciones: form.otras_prestaciones,
      },
      hechos: {
  responsable_despido: form.responsable_despido,
  jefes_inmediatos: form.jefes_inmediatos,
  abogado_contrario: form.abogado_contrario || "",
  narrativa: form.hechos,
},
      condiciones_trabajo: {
        fecha_ingreso: form.fecha_ingreso || null,
        fecha_ultimo_dia_laborado: form.fecha_ultimo_dia_laborado || null,
        puesto: form.puesto,
        salario_diario: form.salario_diario === "" ? null : Number(form.salario_diario),
        salario_diario_integrado: form.salario_diario_integrado === "" ? null : Number(form.salario_diario_integrado),
        jornada: form.jornada,
        dia_descanso: form.dia_descanso,
        lugar_servicio: form.lugar_servicio,
      },
      pruebas_documentacion: {
        tab: "actora",
        checklist: { actora: { ...(form?.pruebas_checklist?.actora || {}) } },
        detalle: { actora: actoraDetalle },
        evidencias_actora: packRows(form.evidencias_actora),
      },
    };

    return { payload, filesMap };
  }, [form, isEdit, idExpediente, idCiudad, idAutoridad, nombreIdentificacionCiudad]);

  const defaultSubmit = async () => {
    // no-op
  };

  const saveTribunal = useCallback(async () => {
    if (submitting) return;

    const ok = validate();
    if (!ok) {
      api.error({
        message: "Faltan campos obligatorios",
        description: "Revisa los campos marcados en rojo.",
        placement: "bottomRight",
        duration: 3,
      });
      return;
    }

    const { payload, filesMap } = buildPayloadAndFiles();

    try {
      setSubmitting(true);
      const fn = onSubmit || defaultSubmit;
      await fn(payload, filesMap);
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Error al guardar. Revisa el servidor.";
      api.error({ message: "No se pudo guardar", description: msg, placement: "bottomRight", duration: 4 });
    } finally {
      setSubmitting(false);
    }
  }, [api, submitting, validate, buildPayloadAndFiles, onSubmit]);

  useEffect(() => {
    const onSaveEvent = () => saveTribunal();
    window.addEventListener("oa:expediente:save", onSaveEvent);
    return () => window.removeEventListener("oa:expediente:save", onSaveEvent);
  }, [saveTribunal]);

  // =========================
  // Autorelleno: fuzzy match + apply
  // =========================
  const normStr = (s) =>
    (s || "")
      .toLowerCase()
      .normalize("NFD")
      .split("")
      .filter((ch) => { const code = ch.charCodeAt(0); return code < 0x0300 || code > 0x036f; })
      .join("")
      .replace(/\s+/g, " ")
      .trim();

  const fuzzyMatchOptions = (needle, options) => {
    if (!needle || !options?.length) return null;
    const n = normStr(needle);
    let best = null;
    let bestScore = 0;
    for (const opt of options) {
      const c = normStr(opt.label || "");
      let score = 0;
      if (c === n) score = 100;
      else if (c.startsWith(n) || n.startsWith(c)) score = 80;
      else if (c.includes(n) || n.includes(c)) score = 60;
      else {
        const nw = n.split(" ").filter((w) => w.length > 2);
        const cw = c.split(" ");
        const exact = nw.filter((w) => cw.includes(w)).length;
        const prefix = nw.filter((nWord) => cw.some((cWord) => cWord.startsWith(nWord) || nWord.startsWith(cWord))).length;
        if (exact > 0) score = 20 + exact * 10;
        else if (prefix > 0) score = 10 + prefix * 8;
      }
      if (score > bestScore) { bestScore = score; best = opt; }
    }
    return bestScore > 0 ? best : null;
  };

  const ACCION_OPTIONS_AF = [
    { label: "DESPIDO", value: "DESPIDO" },
    { label: "RESCISION LABORAL TRABAJADOR", value: "RESCISION LABORAL TRABAJADOR" },
    { label: "REINSTALACION", value: "REINSTALACION" },
    { label: "PAGO DE PRESTACIONES", value: "PAGO DE PRESTACIONES" },
    { label: "DERECHO DE PREFERENCIA Y/O ASCENSO", value: "DERECHO DE PREFERENCIA Y/O ASCENSO" },
  ];

  const PRESTACION_KW = {
    prima_antiguedad: ["antiguedad", "antigüedad", "prima antiguedad", "prima de antiguedad"],
    salarios_caidos: ["salarios caidos", "salarios vencidos", "salarios devengados"],
    vacaciones: ["vacaciones"],
    prima_vacacional: ["prima vacacional"],
    aguinaldo: ["aguinaldo"],
    reparto_utilidades: ["utilidades", "reparto"],
    horas_extras: ["horas extra", "horas extras", "tiempo extra", "sobretiempo", "extraordinari"],
    dias_90_salario: ["90 dias", "90 d", "tres meses", "3 meses", "indemnizacion constitucional", "art. 123", "art 123"],
    dias_20_salario: ["20 dias", "20 d", "veinte dias", "art 50", "art. 50", "articulo 50"],
    reinstalacion: ["reinstalacion", "reinstalación"],
    comisiones: ["comisiones"],
    incripcion_retroactiva_imss: ["imss", "retroactiva", "inscripcion"],
  };

  const DIA_DESCANSO_VALS = ["Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado", "Domingo", "Sin descanso", "Un dia a la semana"];

  const applyAutofill = () => {
    let parsed;
    try {
      parsed = JSON.parse(autofillText);
    } catch (e) {
      setAutofillError("JSON inválido: " + e.message);
      return;
    }
    setAutofillError("");

    // ── Destructure nuevo formato de keys ───────────────────────────────────
    const dg  = parsed?.datos_generales      || {};
    const pl  = parsed?.plazos               || {};
    const ct  = parsed?.condiciones_trabajo  || {};
    const poa = parsed?.pruebas_ofrecidas_actor || {};
    const prestRaw = parsed?.prestaciones    || {};
    const accionRaw = parsed?.accion_intentada ?? "";

    const newAiFields    = new Set();
    const newReviewFields = new Map();
    const markAI     = (key) => newAiFields.add(key);
    const markReview = (key, reason) => newReviewFields.set(key, reason || "Revisar este campo");

    const setAI = (key, val) => {
      if (val != null && val !== "") { setField(key, val); markAI(key); }
    };
    const setAIOrReview = (key, val, reviewMsg) => {
      if (val != null && val !== "") { setField(key, val); markAI(key); }
      else markReview(key, reviewMsg || "Dato no disponible — revisar");
    };

    // --- Estado ---
    let matchedEstadoId = null;
    if (dg.estado) {
      const m = fuzzyMatchOptions(dg.estado, estadosItemsAll.map((e) => ({ label: e.nombre || "", value: e.id })));
      if (m) { matchedEstadoId = m.value; markAI("estado"); }
      else markReview("estado", `No se encontró el estado "${dg.estado}" — seleccionar manualmente`);
    }

    // --- Ciudad ---
    let matchedCiudadId = null;
    if (dg.ciudad && matchedEstadoId != null) {
      const ciudadesEstado = ciudadesItemsAll
        .filter((c) => String(c.id_estado) === String(matchedEstadoId))
        .map((c) => ({ label: c.nombre || "", value: c.id }));
      const m = fuzzyMatchOptions(dg.ciudad, ciudadesEstado);
      if (m) { matchedCiudadId = m.value; markAI("ciudad"); }
      else markReview("ciudad", `No se encontró la ciudad "${dg.ciudad}" — seleccionar manualmente`);
    }

    // --- Autoridad ---
    let matchedAutoridadId = null;
    if (dg.autoridad && matchedCiudadId != null) {
      const autoridadesCiudad = autoridadesItems
        .filter((au) => String(au.id_ciudad) === String(matchedCiudadId))
        .map((au) => ({ label: au.nombre || "", value: String(au.id) }));
      const m = fuzzyMatchOptions(dg.autoridad, autoridadesCiudad);
      if (m) { matchedAutoridadId = m.value; markAI("autoridad"); }
      else markReview("autoridad", `No se encontró la autoridad — seleccionar manualmente`);
    }

    if (matchedEstadoId   != null) { setSelectedEstadoId(matchedEstadoId);     setField("estado",    matchedEstadoId);   }
    if (matchedCiudadId   != null) { setSelectedCiudadId(matchedCiudadId);     setField("ciudad",    matchedCiudadId);   }
    if (matchedAutoridadId != null) { setSelectedAutoridadId(matchedAutoridadId); setField("autoridad", matchedAutoridadId); }

    // --- Empresa + razón social ---
    // dg.empresa  → nombre comercial para buscar en catálogo
    // dg.razon_social → string único de la razón social
    const empresaKeys = Array.from(empresasIndex.keys());
    const empresaNombreRaw = dg.empresa || dg.empresa_nombre || "";
    if (empresaNombreRaw && empresaKeys.length > 0) {
      const mEmpresa = fuzzyMatchOptions(empresaNombreRaw, empresaKeys.map((k) => ({ label: k, value: k })));
      if (mEmpresa) {
        const nombreMatch = mEmpresa.value;
        setField("empresa_nombre", nombreMatch);
        setField("empresa_razon_social_ids", []);
        setField("empresa_id", null);
        markAI("empresa_nombre");

        const bucket = empresasIndex.get(nombreMatch);
        if (bucket) {
          let resolvedEmpresaId = Array.from(bucket.empresaIds || new Set())[0] ?? null;
          // Soportar string, array de strings, o fallback a empresa_razon_social_ids
          const rsRaw = dg.razon_social ?? dg.empresa_razon_social_ids ?? null;
          const rsNames = Array.isArray(rsRaw)
            ? rsRaw.filter(Boolean)
            : rsRaw
            ? [String(rsRaw)]
            : [];
          const matchedRazonValues = [];

          for (const rsName of rsNames) {
            if (!rsName) continue;
            const mRS = fuzzyMatchOptions(String(rsName), bucket.razones.map((r) => ({ label: r.label, value: r.value })));
            if (mRS) {
              const val = String(mRS.value != null ? mRS.value : mRS.label);
              matchedRazonValues.push(val);
              const fullRS = bucket.razones.find((r) => String(r.value != null ? r.value : r.label) === val);
              if (fullRS?.empresa_id != null) resolvedEmpresaId = fullRS.empresa_id;
            }
          }

          if (matchedRazonValues.length > 0) { setField("empresa_razon_social_ids", matchedRazonValues); markAI("empresa_razon_social_ids"); }
          else markReview("empresa_razon_social_ids", "No se encontró la razón social en el catálogo — seleccionar manualmente");
          if (resolvedEmpresaId != null) setField("empresa_id", resolvedEmpresaId);
        }
      } else {
        markReview("empresa_nombre", `No se encontró "${empresaNombreRaw}" en el catálogo — seleccionar manualmente`);
      }
    }

    // --- Datos generales simples ---
    setAI("numero_expediente", dg.numero_expediente);
    setAI("nombre_parte_actora", dg.parte_actora || dg.nombre_parte_actora);
    setAI("num_unico", dg.numero_unico_identificacion || dg.num_unico);
    setAI("corresponsal_nombre", dg.corresponsal || dg.corresponsal_nombre);
    // abogado parte actora → abogado_contrario en el form
    const abogadoVal = dg.abogado_parte_actora || dg.abogado_contrario || "";
    if (abogadoVal) { setField("abogado_contrario", abogadoVal); markAI("abogado_contrario"); }

    // --- Plazos / fechas (nuevo bloque "plazos") ---
    setAIOrReview("fecha_notificacion_demanda",  pl.fecha_notificacion    || pl.fecha_notificacion_demanda,  "Fecha de notificación no encontrada");
    setAIOrReview("fecha_presentacion_demanda",  pl.fecha_presentacion_demanda,  "Fecha de presentación no encontrada");
    setAI("fecha_recepcion_demanda",       pl.fecha_recepcion_en_despacho || pl.fecha_recepcion_demanda);
    setAI("fecha_radicacion_demanda",      pl.fecha_radicacion            || pl.fecha_radicacion_demanda);
    setAI("fecha_emision_citatorio",       pl.fecha_emision_citatorio_ccl || pl.fecha_emision_citatorio);
    setAI("fecha_constancia_conciliacion", pl.fecha_constancia_no_conciliacion || pl.fecha_constancia_conciliacion);
    setAI("fecha_audiencia_conciliatoria", pl.fecha_audiencia_conciliatoria);
    setAI("observaciones_etapa_actual",    pl.observaciones_etapa_actual);
    if (pl.dias_para_contestacion != null || pl.dias_limite != null) {
      const dias = pl.dias_para_contestacion ?? pl.dias_limite;
      if (dias != null) { setField("dias_limite", dias); markAI("dias_limite"); }
    }

    // --- Acción intentada (nivel raíz) ---
    const accionStr = accionRaw || parsed?.acciones_prestaciones?.accion_intentada || "";
    if (accionStr) {
      const mAccion = fuzzyMatchOptions(accionStr, ACCION_OPTIONS_AF);
      if (mAccion) { setField("accion_intentada", mAccion.value); markAI("accion_intentada"); }
      else markReview("accion_intentada", `"${accionStr}" no coincide con ninguna opción — seleccionar manualmente`);
    }

    // --- Prestaciones (nivel raíz, keys propias del nuevo formato) ---
    // Mapa de keys del JSON → keys del form
    const PRESTACION_KEY_MAP = {
      prima_antiguedad:                  "prima_antiguedad",
      salarios_vencidos:                 "salarios_caidos",
      salarios_caidos:                   "salarios_caidos",
      vacaciones:                        "vacaciones",
      prima_vacacional:                  "prima_vacacional",
      aguinaldo:                         "aguinaldo",
      reparto_utilidades:                "reparto_utilidades",
      horas_extras:                      "horas_extras",
      indemnizacion_constitucional_3_meses: "dias_90_salario",
      dias_90_salario:                   "dias_90_salario",
      indemnizacion_art50_20_dias_por_anio: "dias_20_salario",
      dias_20_salario:                   "dias_20_salario",
      reinstalacion:                     "reinstalacion",
      comisiones:                        "comisiones",
      inscripcion_retroactiva_imss:      "incripcion_retroactiva_imss",
      incripcion_retroactiva_imss:       "incripcion_retroactiva_imss",
    };
    const prestSrc = Object.keys(prestRaw).length > 0 ? prestRaw : (parsed?.acciones_prestaciones?.prestaciones || {});
    if (typeof prestSrc === "object" && Object.keys(prestSrc).length > 0) {
      const newPrests = {};
      for (const [rawKey, rawVal] of Object.entries(prestSrc)) {
        if (rawKey === "otras_prestaciones") continue;
        const formKey = PRESTACION_KEY_MAP[rawKey];
        if (formKey && typeof rawVal === "boolean") {
          newPrests[formKey] = rawVal;
        } else if (typeof rawVal === "string" && rawVal) {
          const t = normStr(rawVal);
          for (const [pKey, keywords] of Object.entries(PRESTACION_KW)) {
            if (keywords.some((kw) => t.includes(normStr(kw)))) newPrests[pKey] = true;
          }
        }
      }
      if (Object.keys(newPrests).length > 0) { setField("prestaciones", newPrests); markAI("prestaciones"); }
    }
    const otrasVal = prestRaw.otras_prestaciones || parsed?.acciones_prestaciones?.otras_prestaciones || "";
    setAI("otras_prestaciones", otrasVal);

    // --- Condiciones de trabajo ---
    setAI("fecha_ingreso",            ct.fecha_ingreso);
    setAI("fecha_ultimo_dia_laborado", ct.ultimo_dia_laborado || ct.fecha_ultimo_dia_laborado);
    setAI("puesto",                   ct.puesto_o_categoria   || ct.puesto);
    setAI("jornada",                  ct.jornada);
    setAI("lugar_servicio",           ct.lugar_prestacion_servicio || ct.lugar_servicio);
    setAI("responsable_despido",      ct.responsable_del_despido   || ct.responsable_despido);
    setAI("jefes_inmediatos",         ct.jefes_inmediatos);
    setAI("hechos",                   ct.circunstancias_modo_tiempo_lugar || ct.hechos);
    if (ct.salario_diario != null)            { setField("salario_diario",            String(ct.salario_diario));            markAI("salario_diario"); }
    if (ct.salario_diario_integrado != null)  { setField("salario_diario_integrado",  String(ct.salario_diario_integrado));  markAI("salario_diario_integrado"); }

    // dia_descanso: puede ser string o array
    const diaDescansoRaw = ct.dias_descanso ?? ct.dia_descanso;
    if (diaDescansoRaw != null && diaDescansoRaw !== "") {
      if (Array.isArray(diaDescansoRaw) && diaDescansoRaw.length > 0) {
        setField("dia_descanso", diaDescansoRaw); markAI("dia_descanso");
      } else {
        const diaStr = String(diaDescansoRaw);
        const matched = DIA_DESCANSO_VALS.filter((d) => normStr(diaStr).includes(normStr(d)));
        if (matched.length > 0) { setField("dia_descanso", matched); markAI("dia_descanso"); }
        else markReview("dia_descanso", `"${diaStr}" — seleccionar día de descanso manualmente`);
      }
    }

    // --- Pruebas: checklist ---
    // poa tiene booleanos en el nivel raíz + poa.detalle
    const pruebasBoolMap = {
      confesional:               "confesional",
      testimonial:               "testimonial",
      documental_publica:        "documental_publica",
      documental_privada:        "documental_privada",
      pericial:                  "pericial",
      inspeccion_ocular:         "inspeccion_ocular",
      informe_autoridad:         "informe_autoridad",
      medios_electronicos_ciencia: "medios_electronicos",
      medios_electronicos:       "medios_electronicos",
    };
    const checklistActora = {};
    for (const [jsonKey, formKey] of Object.entries(pruebasBoolMap)) {
      if (typeof poa[jsonKey] === "boolean") checklistActora[formKey] = poa[jsonKey];
    }
    if (Object.keys(checklistActora).length > 0) {
      setForm((p) => ({
        ...p,
        pruebas_checklist: {
          ...(p.pruebas_checklist || {}),
          actora: { ...((p.pruebas_checklist?.actora) || {}), ...checklistActora },
        },
      }));
      markAI("pruebas_checklist");
    }

    // --- Pruebas: detalle ---
    const rawDetalle = poa.detalle || {};
    if (Object.keys(rawDetalle).length > 0) {
      const formActora = {};
      const genId = () => `ai_${Date.now()}_${Math.random().toString(16).slice(2)}`;

      // Construir catálogo de razones sociales para match de confesional
      const rsOptsForConf = [];
      const rsLabelsSeen = new Set();
      for (const bucket of empresasIndex.values()) {
        for (const r of (bucket.razones || [])) {
          const label = r.label || "";
          if (!label || rsLabelsSeen.has(label)) continue;
          rsLabelsSeen.add(label);
          rsOptsForConf.push({ label, value: label });
        }
      }

      // confesional: separar demandado (razon_social) de hechos propios (persona física)
      // Heurística: si descripcion contiene "hechos propios" → confesional_hechos_propios
      const confItems = Array.isArray(rawDetalle.confesional) ? rawDetalle.confesional : [];
      if (confItems.length > 0) {
        const confDemandado = [];
        const confPropios = [];
        for (const item of confItems) {
          const desc = normStr(item.descripcion || "");
          if (desc.includes("hechos propios")) {
            confPropios.push(item);
          } else {
            confDemandado.push(item);
          }
        }
        if (confDemandado.length > 0) {
          formActora.confesional = confDemandado.map((item) => {
            const rsMatch = item.nombre ? fuzzyMatchOptions(item.nombre, rsOptsForConf) : null;
            return { id: genId(), razon_social: rsMatch ? rsMatch.value : undefined };
          });
          // activar checklist si no vino el booleano
          if (checklistActora.confesional == null) {
            setForm((p) => ({ ...p, pruebas_checklist: { ...(p.pruebas_checklist || {}), actora: { ...((p.pruebas_checklist?.actora) || {}), confesional: true } } }));
          }
        }
        if (confPropios.length > 0) {
          formActora.confesional_hechos_propios = confPropios.map((item) => ({
            id: genId(),
            tipo_prueba: item.nombre || "",
            hechos_propios: item.descripcion || "",
            fecha_solicitud: "",
          }));
          setForm((p) => ({ ...p, pruebas_checklist: { ...(p.pruebas_checklist || {}), actora: { ...((p.pruebas_checklist?.actora) || {}), confesional_hechos_propios: true } } }));
        }
      }

      // testimonial
      if (rawDetalle.testimonial) {
        const testigos = (rawDetalle.testimonial.testigos || [])
          .filter((t) => t.nombre)
          .map((t) => ({ id: genId(), nombre: t.nombre || "", relacion: t.relacion || "", contacto: t.contacto || "" }));
        if (testigos.length > 0) formActora.testimonial = { testigos };
      }

      // documentales: añadir id
      const mapDocs = (src) => ({
        ...src,
        documentos: (src?.documentos || []).filter((d) => d.nombre || d.descripcion).map((d) => ({
          id: genId(), nombre: d.nombre || "", descripcion: d.descripcion || "", fecha: d.fecha || "",
        })),
      });
      if (rawDetalle.documental_publica)  formActora.documental_publica  = mapDocs(rawDetalle.documental_publica);
      if (rawDetalle.documental_privada)  formActora.documental_privada  = mapDocs(rawDetalle.documental_privada);
      if (rawDetalle.informe_autoridad) {
        const mapped = mapDocs(rawDetalle.informe_autoridad);
        if (mapped.documentos.length > 0) formActora.informe_autoridad = mapped;
      }

      // pericial
      if (Array.isArray(rawDetalle.pericial)) {
        const items = rawDetalle.pericial.filter((i) => i.tipo_pericial?.length || i.nombre_perito || i.objeto_prueba);
        if (items.length > 0) formActora.pericial = items.map((i) => ({
          id: genId(),
          tipo_pericial: Array.isArray(i.tipo_pericial) ? i.tipo_pericial : [],
          nombre_perito: i.nombre_perito || "",
          objeto_prueba: i.objeto_prueba || "",
        }));
      }

      // inspeccion_ocular
      if (Array.isArray(rawDetalle.inspeccion_ocular)) {
        const items = rawDetalle.inspeccion_ocular.filter((i) => i.tipo_inspeccion_ocular?.length || i.nombre_inspeccion_ocular || i.objeto_prueba);
        if (items.length > 0) formActora.inspeccion_ocular = items.map((i) => ({
          id: genId(),
          tipo_inspeccion_ocular: Array.isArray(i.tipo_inspeccion_ocular) ? i.tipo_inspeccion_ocular : [],
          nombre_inspeccion_ocular: i.nombre_inspeccion_ocular || "",
          objeto_prueba: i.objeto_prueba || "",
        }));
      }

      // medios_electronicos — acepta key "medios_electronicos_ciencia" o "medios_electronicos"
      const mediosRaw = rawDetalle.medios_electronicos_ciencia || rawDetalle.medios_electronicos;
      if (mediosRaw) {
        const rawTipos = Array.isArray(mediosRaw.tipos) ? mediosRaw.tipos : [];
        const matchedTipos = rawTipos.map((t) => fuzzyMatchOptions(t, MEDIOS_OPTS)?.value).filter(Boolean);
        formActora.medios_electronicos = { tipos: matchedTipos, descripcion: mediosRaw.descripcion || "" };
      }

      setForm((p) => ({
        ...p,
        pruebas_detalle: {
          ...(p.pruebas_detalle || {}),
          actora: { ...((p.pruebas_detalle?.actora) || {}), ...formActora },
        },
      }));
      markAI("pruebas_detalle");
    }

    setAiFields(newAiFields);
    setReviewFields(newReviewFields);
    setAutofillOpen(false);
    setAutofillText("");

    const reviewCount = newReviewFields.size;
    api.success({
      message: "Formulario autorrellenado",
      description: reviewCount > 0
        ? `${newAiFields.size} campos llenados. ${reviewCount} campo${reviewCount !== 1 ? "s" : ""} requiere${reviewCount === 1 ? "" : "n"} revisión.`
        : `${newAiFields.size} campos llenados correctamente.`,
      placement: "bottomRight",
      duration: 5,
    });
  };

  // =========================
  // createEmpresa / createRazonSocial placeholders
  // =========================
  const createEmpresaLikeConciliacion = async () => {
    const name = collapseSpaces(newEmpresaNombre);
    if (!name) {
      api.error({
        message: "Falta información",
        description: "Escribe el nombre de la empresa.",
        placement: "bottomRight",
      });
      return;
    }
    api.success({
      message: "Empresa",
      description: "Empresa agregada (conecta el create real aquí).",
      placement: "bottomRight",
    });
    setNewEmpresaNombre("");
  };

  const createRazonSocialLikeConciliacion = async () => {
    const empresaNombre = collapseSpaces(form?.empresa_nombre || "");
    if (!empresaNombre) {
      api.error({
        message: "Falta información",
        description: "Primero selecciona una empresa.",
        placement: "bottomRight",
      });
      return;
    }
    const rs = collapseSpaces(newRazonSocialNombre);
    if (!rs) {
      api.error({
        message: "Falta información",
        description: "Escribe la razón social.",
        placement: "bottomRight",
      });
      return;
    }
    api.success({
      message: "Razón social",
      description: "Razón social agregada (conecta el create real aquí).",
      placement: "bottomRight",
    });
    setNewRazonSocialNombre("");
  };

  // =========================
  // Guardar nueva empresa desde el modal (igual que Centro de Conciliación)
  // =========================
  const handleGuardarNuevaEmpresa = async (payload) => {
    setSavingNuevaEmpresa(true);
    try {
      await dispatch(actionEmpresaCreate(payload));
      await dispatch(actionEmpresasGet());
      api.success({
        message: "Empresa creada",
        description: "Ya puedes seleccionarla en Razón Social.",
        placement: "bottomRight",
      });
      setNuevaEmpresaModal(false);
    } catch (e) {
      api.error({
        message: "Error al crear empresa",
        description: e?.response?.data?.detail || e?.message || "Intenta de nuevo.",
        placement: "bottomRight",
      });
    } finally {
      setSavingNuevaEmpresa(false);
    }
  };

  // =========================
  // fieldMeta: helper para chips y review en secciones
  // =========================
  const fieldMeta = useCallback(
    (key) => ({
      isAI: aiFields.has(key),
      needsReview: reviewFields.has(key),
      reviewReason: reviewFields.get(key) || "",
    }),
    [aiFields, reviewFields]
  );

  const reviewCount = reviewFields.size;
  const goToNextReview = () => {
    const el = document.querySelector(".oa-field-review");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  // =========================
  // RETURN COMPLETO
  // =========================
  return (
    <div className="oa-page">
      {/* ===== MODAL AUTORELLENO ===== */}
      <Modal
        open={autofillOpen}
        onCancel={() => { setAutofillOpen(false); setAutofillError(""); }}
        footer={null}
        width={660}
        centered
        destroyOnClose
        title={null}
        closable={false}
        styles={{ body: { padding: 0 } }}
      >
        <div className="oa-autofill-modal">
          <div className="oa-autofill-header">
            <span className="material-symbols-outlined oa-autofill-icon">auto_awesome</span>
            <div>
              <div className="oa-autofill-title">Autorelleno De Formulario</div>
              <div className="oa-autofill-subtitle">
                Pega el JSON con la información del expediente y se llenarán los campos automáticamente.
                Empresa y razón social se compararán con el catálogo para encontrar la más similar.
              </div>
            </div>
          </div>

          <div className="oa-autofill-body">
            <label className="oa-autofill-label">Datos En Formato JSON</label>
            <textarea
              className={`oa-autofill-textarea${autofillError ? " oa-autofill-textarea-error" : ""}`}
              placeholder={'{\n  "datos_generales": {\n    "numero_expediente": "79/2026",\n    "empresa_nombre": "Farmacia Guadalajara",\n    ...\n  },\n  ...\n}'}
              value={autofillText}
              onChange={(e) => { setAutofillText(e.target.value); if (autofillError) setAutofillError(""); }}
              rows={16}
            />
            {autofillError && (
              <div className="oa-autofill-error">
                <span className="material-symbols-outlined" style={{ fontSize: 15, verticalAlign: "middle", marginRight: 4 }}>error</span>
                {autofillError}
              </div>
            )}
          </div>

          <div className="oa-autofill-footer">
            <button
              type="button"
              className="oa-btn-lite"
              onClick={() => { setAutofillOpen(false); setAutofillText(""); setAutofillError(""); }}
            >
              Cancelar
            </button>
            <button
              type="button"
              className="oa-btn-lite"
              onClick={() => {
                const promptText = `Eres un extractor de datos jurídicos especializado en derecho laboral mexicano. Se te proporcionará el texto de una demanda laboral ante tribunal. Tu única tarea es leerla y devolver exclusivamente el siguiente objeto JSON completamente lleno con la información encontrada — sin explicaciones, sin texto adicional, sin bloques de código markdown, solo el JSON puro.

Reglas de extracción:
- Fechas siempre en formato "YYYY-MM-DD". Si no aparece, usa null.
- Números (salarios, días) como valores numéricos, no strings.
- Si un campo no se encuentra en el documento, deja el valor vacío correspondiente ("", null, o false).
- razon_social: si hay una sola razón social, escríbela como string "Empresa S.A. de C.V.". Si hay más de una, escríbela como array ["Empresa A S.A.", "Empresa B S.A."].
- accion_intentada: describe en texto exactamente la acción ejercida (ej. "Indemnización constitucional por despido injustificado").
- En prestaciones, marca true únicamente las que se reclamen expresamente en la demanda.
- otras_prestaciones: texto libre con prestaciones adicionales que no encajen en los campos booleanos.
- En pruebas_ofrecidas_actor, marca true cada tipo de prueba que la parte actora haya ofrecido. En detalle incluye toda la información específica de cada prueba.
- confesional en detalle: incluye todos los sujetos sobre quienes se ofrece la confesional (representantes legales de empresas y personas físicas por igual), cada uno como un objeto { "nombre": "...", "descripcion": "..." }.
- observaciones_etapa_actual: redacta un resumen claro del estado procesal actual según lo que conste en el documento (acuerdos dictados, emplazamientos, prevenciones, etc.).
- No inventes datos. Si algo es ambiguo o no está, deja el campo vacío.

Devuelve únicamente esto:

{
  "datos_generales": {
    "numero_expediente": "",
    "parte_actora": "",
    "empresa": "",
    "razon_social": "",
    "autoridad": "",
    "ciudad": "",
    "estado": "",
    "abogado_parte_actora": "",
    "corresponsal": "",
    "numero_unico_identificacion": ""
  },
  "plazos": {
    "fecha_notificacion": null,
    "dias_para_contestacion": null,
    "fecha_audiencia_conciliatoria": null,
    "fecha_presentacion_demanda": null,
    "fecha_recepcion_en_despacho": null,
    "fecha_radicacion": null,
    "fecha_emision_citatorio_ccl": null,
    "fecha_constancia_no_conciliacion": null,
    "observaciones_etapa_actual": ""
  },
  "accion_intentada": "",
  "prestaciones": {
    "prima_antiguedad": false,
    "salarios_vencidos": false,
    "vacaciones": false,
    "prima_vacacional": false,
    "aguinaldo": false,
    "reparto_utilidades": false,
    "horas_extras": false,
    "indemnizacion_constitucional_3_meses": false,
    "indemnizacion_art50_20_dias_por_anio": false,
    "reinstalacion": false,
    "comisiones": false,
    "inscripcion_retroactiva_imss": false,
    "otras_prestaciones": ""
  },
  "condiciones_trabajo": {
    "fecha_ingreso": null,
    "ultimo_dia_laborado": null,
    "puesto_o_categoria": "",
    "salario_diario": null,
    "salario_diario_integrado": null,
    "jornada": "",
    "dias_descanso": "",
    "lugar_prestacion_servicio": "",
    "responsable_del_despido": "",
    "jefes_inmediatos": "",
    "circunstancias_modo_tiempo_lugar": ""
  },
  "pruebas_ofrecidas_actor": {
    "confesional": false,
    "testimonial": false,
    "documental_publica": false,
    "documental_privada": false,
    "pericial": false,
    "inspeccion_ocular": false,
    "informe_autoridad": false,
    "medios_electronicos_ciencia": false,
    "detalle": {
      "confesional": [],
      "testimonial": {
        "testigos": []
      },
      "documental_publica": {
        "documentos": []
      },
      "documental_privada": {
        "documentos": []
      },
      "medios_electronicos_ciencia": {
        "tipos": [],
        "descripcion": ""
      }
    }
  }
}`;
                const blob = new Blob([promptText], { type: "text/plain;charset=utf-8" });
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = "prompt_extraccion_demanda.txt";
                a.click();
                URL.revokeObjectURL(url);
              }}
            >
              <span className="material-symbols-outlined" style={{ fontSize: 15, verticalAlign: "middle", marginRight: 4 }}>download</span>
              Descargar Prompt
            </button>
            <button
              type="button"
              className="oa-btn-primary"
              disabled={!autofillText.trim()}
              onClick={applyAutofill}
            >
              <span className="material-symbols-outlined oa-btn-primary-ico">check_circle</span>
              Aceptar Y Rellenar
            </button>
          </div>
        </div>
      </Modal>

      {/* ===== MODAL: Nueva empresa / razón social ===== */}
      <Modal
        open={nuevaEmpresaModal}
        title="Nueva empresa"
        footer={null}
        onCancel={() => setNuevaEmpresaModal(false)}
        width={720}
        destroyOnClose
        centered
      >
        <Spin spinning={savingNuevaEmpresa} tip="Guardando...">
          <FormEmpresas
            ref={nuevaEmpresaRef}
            isEdit={false}
            initialValues={{}}
            onCancel={() => setNuevaEmpresaModal(false)}
            onSaved={() => setNuevaEmpresaModal(false)}
            onSubmit={handleGuardarNuevaEmpresa}
          />
        </Spin>
      </Modal>

      <main className="oa-main">
        {reviewCount > 0 && (
          <div className="oa-autofill-banner">
            <span className="material-symbols-outlined oa-autofill-banner-ico">auto_awesome</span>
            <div className="oa-autofill-banner-text">
              <strong>Formulario precargado por IA</strong> — {reviewCount} campo{reviewCount !== 1 ? "s" : ""} requiere{reviewCount !== 1 ? "n" : ""} revisión
            </div>
            <button type="button" className="oa-autofill-banner-btn" onClick={goToNextReview}>
              Ir al siguiente pendiente
              <span className="material-symbols-outlined" style={{ fontSize: 14 }}>arrow_downward</span>
            </button>
            <button type="button" className="oa-autofill-banner-close" title="Cerrar" onClick={() => setReviewFields(new Map())}>
              <span className="material-symbols-outlined" style={{ fontSize: 16 }}>close</span>
            </button>
          </div>
        )}
        <div className="oa-form">
          {submitting ? (
            <div className="oa-loading-overlay" aria-busy="true" aria-live="polite">
              <div className="oa-loading-card">
                <Spin size="large" />
                <div className="oa-loading-text">Guardando expediente…</div>
              </div>
            </div>
          ) : null}

          <fieldset disabled={submitting} style={{ border: 0, padding: 0, margin: 0 }}>
            {modoRapido && (
              <div className="oa-carga-rapida-banner">
                <span className="material-symbols-outlined" style={{ fontSize: 16 }}>bolt</span>
                <span>
                  <strong>Carga rápida activa</strong> — solo se muestran los campos obligatorios. El expediente quedará marcado como <em>Pendiente de información</em>.
                </span>
              </div>
            )}

            <DatosGeneralesSection
              onOpenAutofill={() => setAutofillOpen(true)}
              fieldMeta={fieldMeta}
              form={form}
              errors={errors}
              modoRapido={modoRapido}
              onToggleModoRapido={onToggleModoRapido}
              isEdit={isEdit}
              expedienteSearchValue={expedienteSearchValue}
              expedienteOptions={expedienteOptions}
              expedienteSearching={expedienteSearching}
              handleExpedienteFocus={handleExpedienteFocus}
              handleExpedienteSearch={handleExpedienteSearch}
              handleExpedienteSelect={handleExpedienteSelect}
              handleExpedienteChange={handleExpedienteChange}
              selectedEstadoId={selectedEstadoId}
              selectedCiudadId={selectedCiudadId}
              selectedAutoridadId={selectedAutoridadId}
              estadosOptionsFinal={estadosOptionsFinal}
              ciudadesOptionsFinal={ciudadesOptionsFinal}
              autoridadOptions={autoridadOptions}
              handleChangeEstado={handleChangeEstado}
              handleChangeCiudad={handleChangeCiudad}
              handleChangeAutoridad={handleChangeAutoridad}
              allRazonesSociales={allRazonesSociales}
              handleChangeRazonSocial={handleChangeRazonSocial}
              onOpenNuevaEmpresa={() => setNuevaEmpresaModal(true)}
              setField={setField}
            />

            <EtapasCitatorioSection
              fieldMeta={fieldMeta}
              form={form}
              errors={errors}
              submitting={submitting}
              modoRapido={modoRapido}
              setField={setField}
              openCitatorioPicker={openCitatorioPicker}
              onCitatorioPicked={onCitatorioPicked}
              viewCitatorio={viewCitatorio}
              deleteCitatorio={deleteCitatorio}
              citatorioInputRef={citatorioInputRef}
            />

            <AccionesPrestacionesSection
              fieldMeta={fieldMeta}
              form={form}
              errors={errors}
              setField={setField}
              setPrestacion={setPrestacion}
              modoRapido={modoRapido}
              accionesOptions={accionesIntentadasCatalogo}
              prestacionesCatalogo={prestacionesCatalogo}
            />

            {!modoRapido && <HechosSection fieldMeta={fieldMeta} form={form} errors={errors} setField={setField} />}

            {!modoRapido && <CondicionesTrabajoSection fieldMeta={fieldMeta} form={form} errors={errors} setField={setField} />}

           {!modoRapido && <PruebasSection
  form={form}
  checklistActivo={checklistActivo}
  setPruebasDetalle={setPruebasDetalle}
  removeTestigo={removeTestigo}
  catalogOverrides={catalogOverrides}
  setCatalogOverrides={setCatalogOverrides}
  collapseSpaces={collapseSpacesLocal}
  makeNewSelectValue={makeNewSelectValue}
  MEDIOS_OPTS={MEDIOS_OPTS}
  toggleMedioElectronico={toggleMedioElectronico}
  setMediosElectronicosTipos={setMediosElectronicosTipos}
  toggleChecklistActora={toggleChecklistActora}
  errors={errors}
  api={api}
  empresaNombreOptions={empresaNombreOptions}
  empresasIndex={empresasIndex}
/>}

            <div className="oa-hidden-actions">
              <button type="button" onClick={onCancel}>
                Cancelar
              </button>
              <button type="button" disabled={submitting} onClick={saveTribunal}>
                {submitting ? "Guardando..." : "Guardar expediente"}
              </button>
            </div>
          </fieldset>
        </div>
      </main>
    </div>
  );
}